/**
 * Utility functions for shadcn/ui components.
 * @AF1.0
 */
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatMoney } from "@goppy/ui-kit/format";

/**
 * Merge Tailwind CSS classes with clsx.
 * Handles conditional classes and resolves Tailwind conflicts.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Tone semantic dùng cho Badge/health/KPI (US-AE.F0-0000-S03). */
export type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "primary";

/** Mã tiền tệ ISO 4217 đúng dạng (CHECK `^[A-Z]{3}$` của DB). */
const CURRENCY_CODE_RE = /^[A-Z]{3}$/;
/** Số nguyên minor dạng chuỗi (contract tiền trên wire: string minor, dấu âm cho phép). */
const MINOR_STRING_RE = /^-?\d+$/;
/** Ô tiền không in được số thật (vắng · bị ẩn theo vai · rác) — KHÔNG "0" (0 = "không đồng nào", nói dối về tiền). */
export const MONEY_PLACEHOLDER = "—";

/**
 * Giá trị ô tiền → minor units BigInt, hoặc `null` khi không phải số nguyên minor hợp lệ.
 * Nhận: bigint · chuỗi `^-?\d+$` (wire) · number NGUYÊN AN TOÀN (MOCK/prototype). Mọi thứ khác (null, "", "3.5", "1e6",
 * 1.5, NaN, 2^53…) ⇒ `null` — không đoán, không làm tròn, không Number() chuỗi (mất chữ số > 2^53).
 */
export function parseMinorUnits(v: unknown): bigint | null {
  if (typeof v === "bigint") return v;
  if (typeof v === "string") return MINOR_STRING_RE.test(v) ? BigInt(v) : null;
  if (typeof v === "number") return Number.isSafeInteger(v) ? BigInt(v) : null;
  return null;
}

/**
 * Chữ hiển thị cho ô TIỀN (bảng engine + xuất CSV) — MỘT nguồn: `formatMoney` BigInt của `@goppy/ui-kit`, cùng nguồn với
 * `MoneyAmount` ở các trang chi tiết (ADMIN-AI-AUDIT-AMOUNT-DIV100: danh sách và chi tiết từng in hai số khác nhau).
 * `v` là minor units (VND: minor = đồng, không có xu ⇒ KHÔNG chia 100). `currency` theo dòng; vắng ⇒ VND (cột tiền của
 * engine là VND khi nguồn không khai tiền tệ — như màn chi tiết); có mà sai dạng ⇒ "—" (không đoán đơn vị).
 * CHỈ format — KHÔNG tính toán tiền ở client (bất biến TDD §2).
 */
export function moneyCellText(v: unknown, currency: unknown = "VND"): string {
  const minor = parseMinorUnits(v);
  if (minor === null) return MONEY_PLACEHOLDER;
  const code = currency === undefined ? "VND" : currency;
  if (typeof code !== "string" || !CURRENCY_CODE_RE.test(code)) return MONEY_PLACEHOLDER;
  return formatMoney(minor, code as Parameters<typeof formatMoney>[1]);
}

/**
 * Props a11y cho hàng bảng click-được (board dựng `<table>` riêng, không qua DataTable).
 * role=button + tabIndex + Enter/Space kích hoạt → keyboard-only dùng được (mirror fix DataTable).
 */
export function rowClickProps(onActivate: () => void): {
  role: "button";
  tabIndex: 0;
  onClick: () => void;
  onKeyDown: (e: { key: string; preventDefault: () => void }) => void;
} {
  return {
    role: "button",
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onActivate();
      }
    },
  };
}
